import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from './shared/Button';
import { DownloadSection } from './shared/DownloadSection';
import { FeedbackWidget, ExpiredLinkIcon } from './shared/FeedbackWidget';
import { GiftBoxIcon } from './icons';
import { LoadingWidget } from './shared/LoadingWidget';
import { detectPlatform } from '../utils/platform';
import { buildPlayStoreUrl, buildIosDeepLink, buildAndroidIntentUrl, waitForAppOpen, APP_STORE_URL } from '../utils/deepLink';

type ScreenState = 'loading' | 'invalid' | 'clipboard-fallback' | 'other';

const APP_OPEN_TIMEOUT_MS = 1500;

export const RedirectScreen: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const phone = searchParams.get('phone');

    const [screenState, setScreenState] = useState<ScreenState>('loading');
    const [deepLink, setDeepLink] = useState('');
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        if (!token) {
            setScreenState('invalid');
            return;
        }

        const platform = detectPlatform();

        if (platform === 'android') {
            // Try handing the tap straight to the app; Chrome falls back to
            // the Play Store URL itself if it's not installed. The extra
            // timer below covers browsers that don't understand intent://.
            window.location.href = buildAndroidIntentUrl(token, phone);
            waitForAppOpen(APP_OPEN_TIMEOUT_MS).then((opened) => {
                if (!opened) {
                    window.location.href = buildPlayStoreUrl(token, phone);
                }
            });
            return;
        }

        if (platform === 'ios') {
            const link = buildIosDeepLink(token, phone);
            setDeepLink(link);
            window.location.href = link;
            waitForAppOpen(APP_OPEN_TIMEOUT_MS).then(async (opened) => {
                if (opened) return;
                try {
                    await navigator.clipboard.writeText(link);
                    window.location.href = APP_STORE_URL;
                } catch {
                    setScreenState('clipboard-fallback');
                }
            });
            return;
        }

        if (platform === 'mac') {
            // No Mac build to hand the scheme off to — go straight to the
            // clipboard/App Store handoff, same as before.
            const link = buildIosDeepLink(token, phone);
            setDeepLink(link);
            (async () => {
                try {
                    await navigator.clipboard.writeText(link);
                    window.location.href = APP_STORE_URL;
                } catch {
                    setScreenState('clipboard-fallback');
                }
            })();
            return;
        }

        setScreenState('other');
    }, [token, phone]);

    const handleContinueToAppStore = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(deepLink);
        } catch {
            console.error('Failed to copy deep link to clipboard');
        }
        window.location.href = APP_STORE_URL;
    }, [deepLink]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            padding: '20px 16px',
            maxWidth: '400px',
            margin: '0 auto',
            boxSizing: 'border-box',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <img
                    src="/images/icons/pe.svg"
                    alt="Logo"
                    style={{ width: '40px', height: '40px' }}
                />
            </div>

            {/* Main content — vertically centered */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {screenState === 'loading' && <LoadingWidget message="Redirecting..." />}
                {screenState === 'invalid' && (
                    <FeedbackWidget
                        icon={<ExpiredLinkIcon />}
                        title="Invalid link"
                        message="This link is missing required information."
                    />
                )}
                {screenState === 'clipboard-fallback' && (
                    <ClipboardFallbackWidget
                        onContinue={handleContinueToAppStore}
                    />
                )}
                {screenState === 'other' && <DownloadSection />}
            </div>
        </div>
    );
};

const ClipboardFallbackWidget: React.FC<{
    onContinue: () => void;
}> = ({ onContinue }) => (
    <div style={{
        textAlign: 'center',
        backgroundColor: '#161616',
        borderRadius: '16px',
        padding: '32px 24px',
        color: '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
    }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <GiftBoxIcon />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
            You're almost set up
        </h2>
        <p style={{ fontSize: '14px', color: '#9ca3af', margin: '0 0 20px 0' }}>
            Head to the App Store to finish creating your account.
        </p>
        <Button onClick={onContinue}>Continue to App Store</Button>
    </div>
);
